import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Settings, FileText, DollarSign, Plus, Edit2, Trash2, 
  Loader2, Save, AlertCircle, Check, X, ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import AdminLayout from '../../components/AdminLayout';
import { SidebarProvider } from '../../components/AdminSidebarNew';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const TABS = [
  { id: 'config', label: 'Configuración', icon: Settings },
  { id: 'policies', label: 'Políticas', icon: FileText },
];

const POLICY_TYPES = [
  { value: 'general', label: 'General' },
  { value: 'press', label: 'Prensa' },
  { value: 'producer', label: 'Productoras' },
  { value: 'communication', label: 'Comunicación' },
];

const PlatformConfigPage = () => {
  const [activeTab, setActiveTab] = useState('config');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Config state
  const [config, setConfig] = useState({
    service_fee_percent: 10,
    min_package_price: 1000,
    currency: 'ARS'
  });
  
  // Policies state
  const [policies, setPolicies] = useState([]);
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [policyForm, setPolicyForm] = useState({
    title: '',
    content: '',
    policy_type: 'general',
    is_active: true,
    order: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [configRes, policiesRes] = await Promise.all([
        api.get('/admin/config').catch(() => ({ data: config })),
        api.get('/admin/policies').catch(() => ({ data: [] }))
      ]);
      
      setConfig(configRes.data);
      setPolicies(policiesRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      await api.put('/admin/config', {
        service_fee_percent: parseFloat(config.service_fee_percent)
      });
      toast.success('Configuración guardada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePolicy = async () => {
    if (!policyForm.title.trim() || !policyForm.content.trim()) {
      toast.error('Completá el título y contenido');
      return;
    }
    
    setSaving(true);
    try {
      if (editingPolicy) {
        await api.put(`/admin/policies/${editingPolicy.id}`, policyForm);
        toast.success('Política actualizada');
      } else {
        await api.post('/admin/policies', policyForm);
        toast.success('Política creada');
      }
      setShowPolicyModal(false);
      setEditingPolicy(null);
      resetPolicyForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePolicy = async (policyId) => {
    if (!confirm('¿Eliminar esta política?')) return;
    
    try {
      await api.delete(`/admin/policies/${policyId}`);
      toast.success('Política eliminada');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const openEditPolicy = (policy) => {
    setEditingPolicy(policy);
    setPolicyForm({
      title: policy.title,
      content: policy.content,
      policy_type: policy.policy_type || 'general',
      is_active: policy.is_active !== false,
      order: policy.order || 0
    });
    setShowPolicyModal(true);
  };

  const resetPolicyForm = () => {
    setPolicyForm({
      title: '',
      content: '',
      policy_type: 'general',
      is_active: true,
      order: 0
    });
  };

  return (
    <SidebarProvider>
      <AdminLayout>
        <div className="p-6 max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl font-bold text-white">Configuración de Plataforma</h1>
              <p className="text-zinc-500 mt-1">Administra comisiones y políticas</p>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  activeTab === tab.id
                    ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                    : 'bg-zinc-800/50 text-zinc-400 hover:bg-zinc-800 border border-transparent'
                }`}
              >
                <tab.icon size={18} />
                {tab.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="animate-spin text-blue-500" size={32} />
            </div>
          ) : (
            <>
              {/* Config Tab */}
              {activeTab === 'config' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6"
                >
                  <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
                    <h2 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                      <DollarSign className="text-green-400" size={20} />
                      Costos de Servicio de Plataforma
                    </h2>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label className="text-zinc-400">Costo de Servicio (%)</Label>
                        <div className="flex gap-2 mt-1">
                          <Input
                            type="number"
                            min="0"
                            max="50"
                            step="0.5"
                            value={config.service_fee_percent}
                            onChange={(e) => setConfig({ ...config, service_fee_percent: e.target.value })}
                            className="bg-zinc-800 border-zinc-700"
                          />
                          <span className="bg-zinc-700 border border-zinc-600 rounded-md px-4 flex items-center text-zinc-300">
                            %
                          </span>
                        </div>
                        <p className="text-zinc-500 text-xs mt-2">
                          Porcentaje que se cobra por cada transacción en el marketplace de prensa
                        </p>
                      </div>
                      
                      <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
                        <h4 className="text-blue-400 font-medium mb-2 flex items-center gap-2">
                          <AlertCircle size={16} />
                          Ejemplo
                        </h4>
                        <p className="text-zinc-400 text-sm">
                          Con <span className="text-white font-bold">{config.service_fee_percent}%</span> de costo de servicio:
                        </p>
                        <ul className="text-zinc-400 text-sm mt-2 space-y-1">
                          <li>• Cotización: $10.000</li>
                          <li>• Costo de servicio: ${(10000 * (config.service_fee_percent / 100)).toLocaleString()}</li>
                          <li>• Total productor: ${(10000 * (1 + config.service_fee_percent / 100)).toLocaleString()}</li>
                        </ul>
                      </div>
                    </div>
                    
                    <div className="mt-6 pt-6 border-t border-zinc-800">
                      <Button 
                        onClick={handleSaveConfig}
                        disabled={saving}
                        className="bg-blue-500 hover:bg-blue-600"
                      >
                        {saving ? (
                          <Loader2 className="animate-spin mr-2" size={16} />
                        ) : (
                          <Save size={16} className="mr-2" />
                        )}
                        Guardar Configuración
                      </Button>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Policies Tab */}
              {activeTab === 'policies' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6"
                >
                  <div className="flex justify-between items-center">
                    <h2 className="text-lg font-bold text-white flex items-center gap-2">
                      <FileText className="text-purple-400" size={20} />
                      Políticas de Plataforma
                    </h2>
                    <Button 
                      onClick={() => {
                        setEditingPolicy(null);
                        resetPolicyForm();
                        setShowPolicyModal(true);
                      }}
                      className="bg-purple-500 hover:bg-purple-600"
                    >
                      <Plus size={16} className="mr-2" />
                      Nueva Política
                    </Button>
                  </div>
                  
                  {policies.length === 0 ? (
                    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-12 text-center">
                      <FileText className="mx-auto mb-4 text-zinc-600" size={48} />
                      <p className="text-zinc-500">No hay políticas creadas</p>
                      <p className="text-zinc-600 text-sm mt-1">Creá políticas para prensa, productoras o comunicación</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {policies.map((policy) => (
                        <div 
                          key={policy.id}
                          className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5"
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="text-white font-bold">{policy.title}</h3>
                                <span className={`px-2 py-1 rounded-full text-xs border ${
                                  policy.is_active 
                                    ? 'bg-green-500/20 text-green-400 border-green-500/30'
                                    : 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30'
                                }`}>
                                  {policy.is_active ? 'Activa' : 'Inactiva'}
                                </span>
                                <span className="px-2 py-1 rounded-full text-xs bg-zinc-800 text-zinc-400">
                                  {POLICY_TYPES.find(t => t.value === policy.policy_type)?.label || policy.policy_type}
                                </span>
                              </div>
                              <p className="text-zinc-400 text-sm mt-2 whitespace-pre-wrap line-clamp-3">
                                {policy.content}
                              </p>
                              <p className="text-zinc-600 text-xs mt-2">
                                Orden: {policy.order} • Creado: {new Date(policy.created_at).toLocaleDateString()}
                              </p>
                            </div>
                            
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => openEditPolicy(policy)}
                              >
                                <Edit2 size={14} />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                className="text-red-400 hover:bg-red-500/10"
                                onClick={() => handleDeletePolicy(policy.id)}
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </motion.div>
              )}
            </>
          )}
        </div>

        {/* Policy Modal */}
        <Dialog open={showPolicyModal} onOpenChange={setShowPolicyModal}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">
                {editingPolicy ? 'Editar Política' : 'Nueva Política'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-zinc-400">Título *</Label>
                <Input
                  value={policyForm.title}
                  onChange={(e) => setPolicyForm({ ...policyForm, title: e.target.value })}
                  placeholder="Ej: Normas de Comunicación para Prensa"
                  className="bg-zinc-800 border-zinc-700 mt-1"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-400">Tipo de Política</Label>
                  <Select 
                    value={policyForm.policy_type}
                    onValueChange={(v) => setPolicyForm({ ...policyForm, policy_type: v })}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      {POLICY_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-zinc-400">Orden</Label>
                  <Input
                    type="number"
                    min="0"
                    value={policyForm.order}
                    onChange={(e) => setPolicyForm({ ...policyForm, order: parseInt(e.target.value) || 0 })}
                    className="bg-zinc-800 border-zinc-700 mt-1"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-zinc-400">Contenido *</Label>
                <Textarea
                  value={policyForm.content}
                  onChange={(e) => setPolicyForm({ ...policyForm, content: e.target.value })}
                  placeholder="Escribe el contenido de la política..."
                  className="bg-zinc-800 border-zinc-700 mt-1 min-h-[200px]"
                />
              </div>
              
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={policyForm.is_active}
                  onChange={(e) => setPolicyForm({ ...policyForm, is_active: e.target.checked })}
                  className="rounded bg-zinc-700 border-zinc-600"
                />
                <Label htmlFor="is_active" className="text-zinc-400 cursor-pointer">
                  Política activa (visible públicamente)
                </Label>
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setShowPolicyModal(false)}
                >
                  Cancelar
                </Button>
                <Button 
                  className="flex-1 bg-blue-500 hover:bg-blue-600"
                  onClick={handleSavePolicy}
                  disabled={saving}
                >
                  {saving ? (
                    <Loader2 className="animate-spin mr-2" size={16} />
                  ) : (
                    <Save size={16} className="mr-2" />
                  )}
                  {editingPolicy ? 'Actualizar' : 'Crear'} Política
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </AdminLayout>
    </SidebarProvider>
  );
};

export default PlatformConfigPage;
